import { useSyncExternalStore } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { LogOut, Monitor, Store, User } from "lucide-react";

import { cn, displayName } from "@/lib/utils";
import { useIdentities, useLogout, useMe } from "../../api/queries.ts";
import {
  onIdentityChanged,
  readIdentity,
  writeIdentity,
  writePurchaseAs,
} from "../../lib/identity.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  顶栏「账号」的悬停卡片
 * ═══════════════════════════════════════════════════════════════
 *
 * 鼠标滑过顶栏右上角的名字 → 掀开一张卡：**切换身份** + **退出登录**。
 * （参考 twshangcheng 的 `AccountPreview`，那边是会员卡 + 登出。）
 *
 * 🔴 **切身份必须能在这儿做。** 陈总有三样东西（我自己买东西 / 我的店 /
 *    平台后台），原先只有进「我的」那一页才切得了 ——
 *    而"我现在看到的是哪个价"是逛商城时随时要确认、随时要换的事，
 *    让他为了换个价格视角先跳去账户页，等于每次都要绕一圈。
 *
 * 🔴 **用 CSS `group-hover`，不要用 React state。**
 *    鼠标滑进滑出都会 `setState` → 整个顶栏重渲染，
 *    而顶栏里有搜索框（受控输入）和购物车数量。
 *
 * ⚠️ **卡片挂在外层 `div` 上，不能放进 `<Link>` 里。**
 *    放进去的话卡片里的"切身份""退出登录"都成了链接里的按钮 ——
 *    点它们会同时触发外层那条去 `/me` 的跳转。
 *
 * ⚠️ **浮层和图标之间不能留缝**：用 `pt-2`（内边距）撑开视觉间距，
 *    不要用 `mt-2`（外边距）—— 留了缝，鼠标往下移的途中就离开了 hover 区，
 *    卡片当场收掉，根本点不到里面的东西。
 *
 * ⚠️ **只在有鼠标的设备上出现**（`hidden lg:block`）：触屏没有 hover，
 *    在那儿点一下名字直接进「我的」才是对的。
 */

/** 当前选中的身份。⚠️ 它存在 localStorage 里，不是 React state，必须订阅事件。 */
function useIdentity(): string | null {
  return useSyncExternalStore(onIdentityChanged, readIdentity, () => null);
}

export function AccountMenu() {
  const { data: me } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const identity = useIdentity();
  const logout = useLogout();
  const navigate = useNavigate();

  if (!me) {
    return (
      <Link
        to="/login"
        className="flex h-10 items-center rounded-lg bg-brand-600 px-4 text-[14px] font-medium text-white transition-colors hover:bg-brand-700"
      >
        登录 / 注册
      </Link>
    );
  }

  const orgs = identities?.orgs ?? [];
  const activeOrg = orgs.find((o) => String(o.id) === identity) ?? null;
  const name = displayName(me);

  /** ⚠️ 切完不用手动 invalidate —— 价格视角进了查询 key，换视角天然是另一批缓存。 */
  const pick = (v: string) => writeIdentity(v);

  return (
    <div className="group/account relative">
      {/*
        ⚠️ 名字本身仍然是**一条链接**：触屏和键盘用户点它直接进「我的」。
           悬停卡是给鼠标的额外快捷方式，不是唯一的路。
      */}
      <Link
        to="/me"
        className="flex h-10 items-center gap-2 rounded-lg px-3 text-[14px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <User className="size-[18px]" />
        {name}
        {/* ⚠️ 顶栏上就标出当前身份 —— 否则他得掀开卡片才知道自己在看谁的价。 */}
        {activeOrg && (
          <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[11px] text-brand-700">
            {activeOrg.name}
          </span>
        )}
      </Link>

      <div className="pointer-events-none absolute top-full right-0 z-50 hidden pt-2 opacity-0 transition-opacity duration-150 group-hover/account:pointer-events-auto group-hover/account:opacity-100 lg:block">
        <div className="w-[268px] overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          <div className="border-b border-border px-4 py-3.5">
            <p className="truncate text-[14px] font-medium">{name}</p>
            <p className="mt-0.5 truncate text-[12px] text-muted-foreground">
              {me.phone ?? me.email}
            </p>
          </div>

          {/*
            🔴 **三样东西，三个选项。** 老板：「陈总自己可以逛装修商店…
               他也可以有自己的店…，管理平台。这是三个东西，不要混在一起」。
               这里跟「我的」那一页的切换器是同一套语义，只是换了个位置。
          */}
          {orgs.length > 0 && (
            <div className="border-b border-border p-1.5">
              <p className="px-2.5 pt-1 pb-1.5 text-[11px] tracking-wider text-muted-foreground">
                当前身份
              </p>
              <button
                onClick={() => pick("")}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors",
                  !activeOrg
                    ? "bg-brand-50 font-medium text-brand-700"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                )}
              >
                <User className="size-4 shrink-0" />
                {/*
                  🔴 **三行是「我是买家 / 我是卖家 / 平台管理」**（老板定的字眼）。
                     说的是**这一刻我以什么身份在用这个站**，不是"我是谁"——
                     「我自己」那种说法只说了是谁，没说来干什么。
                  ⚠️ 后面原来挂着一个「进货价」小标，删了：价格是什么价，
                     页面顶部那条横幅已经说过一次，每一处再标一遍就是噪音。
                */}
                <span className="min-w-0 flex-1 truncate text-left">我是买家</span>
              </button>
              {orgs.map((o) => (
                <button
                  key={o.id}
                  onClick={() => pick(String(o.id))}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors",
                    activeOrg?.id === o.id
                      ? "bg-brand-50 font-medium text-brand-700"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                  )}
                >
                  <Store className="size-4 shrink-0" />
                  {/*
                    ⚠️ 右边固定写「我的店铺」。老板：「一个人不会有很多店，只有一个」——
                       所以不用为多店留后路，这里也就不需要拿店名去区分。
                  */}
                  <span className="min-w-0 flex-1 truncate text-left">我是卖家</span>
                  <span className="shrink-0 text-[11px] opacity-70">我的店铺</span>
                </button>
              ))}
              {/* ⚠️ 后台是**跳走**不是切身份 —— 它是另一套界面，不是「我的」的一个视图。 */}
              {orgs.some((o) => o.type === "platform") && (
                <Link
                  to="/admin"
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <Monitor className="size-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-left">平台管理</span>
                </Link>
              )}
            </div>
          )}

          <div className="p-1.5">
            <button
              disabled={logout.isPending}
              onClick={async () => {
                await logout.mutateAsync();
                /**
                 * ⚠️ **退出时把身份一起清掉。**
                 *    不清的话，下一个人在这台电脑上登录会带着上一个人的采购身份逛商城 ——
                 *    价格是别人的进货价。
                 */
                writeIdentity("");
                writePurchaseAs(null);
                /** ⚠️ 退出之后落到登录页，不是首页 —— 下一步几乎总是"换个号登进来"。 */
                void navigate({ to: "/login" });
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
            >
              <LogOut className="size-4 shrink-0" />
              {logout.isPending ? "退出中…" : "退出登录"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
